import React, { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import axios from "axios";
import { API_BASE_URL } from "../config/api";

function ExhibitorsPrint() {
  const [searchParams] = useSearchParams();
  const companyFilter = searchParams.get("company") || "all";

  const [cards, setCards] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    const fetchExhibitors = async () => {
      try {
        setLoading(true);
        const res = await axios.get(`${API_BASE_URL}/api/registrations`);
        let confirmed = res.data.filter((item) => item.status === "Confirmed");

        if (companyFilter !== "all") {
          confirmed = confirmed.filter(
            (item) => item.company && item.company.trim() === companyFilter.trim()
          );
        }

        confirmed.sort((a, b) => (a.company || "").localeCompare(b.company || ""));

        const allCards = [];
        for (let i = 0; i < confirmed.length; i++) {
          const item = confirmed[i];
          try {
            const qrRes = await axios.get(`${API_BASE_URL}/api/generate-qr/${item.id}`);
            if (qrRes.data.exhibitors && qrRes.data.exhibitors.length > 0) {
              qrRes.data.exhibitors.forEach((ex) => {
                allCards.push({
                  id: ex.id || `${item.id}_${allCards.length}`,
                  name: ex.name || item.name || "",
                  company: ex.company || item.company || "",
                  qrUrl: `${API_BASE_URL}/qrcodes/${ex.fileName || ex.file}`
                });
              });
            } else {
              allCards.push({
                id: item.id,
                name: item.name || "",
                company: item.company || "",
                qrUrl: `${API_BASE_URL}/qrcodes/${qrRes.data.file}`
              });
            }
          } catch (err) {
            console.error(`Error fetching QR for item ${item.id}`, err);
          }
        }

        setCards(allCards);
      } catch (err) {
        console.error("Error fetching registrations for print", err);
        setError("Failed to load exhibitors data.");
      } finally {
        setLoading(false);
      }
    };

    fetchExhibitors();
  }, [companyFilter]);

  useEffect(() => {
    if (!loading && cards.length > 0) {
      const timer = setTimeout(() => {
        window.print();
      }, 600);
      return () => clearTimeout(timer);
    }
  }, [loading, cards]);

  const handlePrint = () => {
    window.print();
  };

  return (
    <div style={{ background: "#f8fafc", minHeight: "100vh", fontFamily: "'Montserrat', sans-serif" }}>
      {/* Non-printable Control Bar */}
      <div
        className="no-print"
        style={{
          position: "sticky",
          top: 0,
          zIndex: 100,
          background: "#1e293b",
          color: "#fff",
          padding: "12px 24px",
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          boxShadow: "0 4px 12px rgba(0,0,0,0.15)"
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
          <h4 style={{ margin: 0, fontWeight: "700", fontSize: "1.2rem", color: "#fff" }}>
            Exhibitors Print Preview
          </h4>
          <span
            style={{
              background: "#3b82f6",
              color: "#fff",
              padding: "4px 10px",
              borderRadius: "12px",
              fontSize: "0.85rem",
              fontWeight: "600"
            }}
          >
            {companyFilter === "all" ? "All Companies" : companyFilter} ({cards.length})
          </span>
        </div>
        <div style={{ display: "flex", gap: "10px" }}>
          <button
            onClick={handlePrint}
            disabled={loading || cards.length === 0}
            style={{
              background: "#22c55e",
              color: "#fff",
              border: "none",
              padding: "8px 20px",
              borderRadius: "6px",
              fontWeight: "700",
              cursor: "pointer",
              fontSize: "0.95rem",
              display: "flex",
              alignItems: "center",
              gap: "8px"
            }}
          >
            <i className="fas fa-print"></i> Print
          </button>
          <button
            onClick={() => window.close()}
            style={{
              background: "#64748b",
              color: "#fff",
              border: "none",
              padding: "8px 16px",
              borderRadius: "6px",
              fontWeight: "600",
              cursor: "pointer",
              fontSize: "0.9rem"
            }}
          >
            Close
          </button>
        </div>
      </div>

      {/* Main Content Area */}
      <div style={{ padding: "20px" }}>
        {loading ? (
          <div className="no-print text-center py-5">
            <div className="spinner-border text-primary" role="status" style={{ width: "3rem", height: "3rem" }}>
              <span className="visually-hidden">Loading...</span>
            </div>
            <p className="mt-3 fw-bold text-secondary">Loading exhibitor QR cards...</p>
          </div>
        ) : error ? (
          <div className="no-print alert alert-danger text-center my-4">{error}</div>
        ) : cards.length === 0 ? (
          <div className="no-print alert alert-warning text-center my-4">
            No confirmed exhibitors found for selected company filter.
          </div>
        ) : (
          <div className="print-container">
            {cards.map((card, idx) => (
              <div key={idx} className="print-card">
                <div className="qr-image-box">
                  <img src={card.qrUrl} alt={card.name} />
                </div>
                <div className="person-name">{card.name || "—"}</div>
                <div className="company-name">{card.company || "—"}</div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* CSS Styles for Screen and Print */}
      <style>{`
        .print-container {
          display: flex;
          flex-wrap: wrap;
          gap: 20px;
          justify-content: center;
          max-width: 1200px;
          margin: 0 auto;
        }

        .print-card {
          width: 280px;
          background: #ffffff;
          border: 1px solid #e2e8f0;
          border-radius: 12px;
          padding: 20px 16px;
          text-align: center;
          box-shadow: 0 4px 6px -1px rgba(0,0,0,0.05);
          display: flex;
          flex-direction: column;
          align-items: center;
          box-sizing: border-box;
          page-break-inside: avoid;
          break-inside: avoid;
        }

        .qr-image-box {
          background: #ffffff;
          padding: 8px;
          border-radius: 8px;
          margin-bottom: 12px;
        }

        .qr-image-box img {
          width: 200px;
          height: 200px;
          object-fit: contain;
          display: block;
        }

        .person-name {
          font-size: 1.1rem;
          font-weight: 700;
          color: #0f172a;
          margin-bottom: 4px;
          word-break: break-word;
          line-height: 1.3;
        }

        .company-name {
          font-size: 1rem;
          font-weight: 800;
          color: #1e293b;
          word-break: break-word;
          line-height: 1.3;
        }

        @media print {
          @page {
            size: auto;
            margin: 10mm;
          }

          body {
            background: #ffffff !important;
            margin: 0 !important;
            padding: 0 !important;
          }

          .no-print {
            display: none !important;
          }

          .print-container {
            display: flex !important;
            flex-wrap: wrap !important;
            gap: 15px !important;
            justify-content: flex-start !important;
            max-width: 100% !important;
            margin: 0 !important;
          }

          .print-card {
            width: 48% !important;
            box-shadow: none !important;
            border: 1px solid #cbd5e1 !important;
            margin-bottom: 15px !important;
            page-break-inside: avoid !important;
            break-inside: avoid !important;
          }
        }
      `}</style>
    </div>
  );
}

export default ExhibitorsPrint;
